"use client";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import { useRouter } from "next/navigation";
import { useTheme } from "next-themes";
import {
  LogOut,
  Sun,
  Moon,
  Menu,
  Loader,
  LayoutDashboard,
  ShieldCheck,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { createSupabaseBrowserClient } from "@/lib/auth/supabase-browser";
import { useEffect, useState, useCallback, useRef } from "react";
import { GenericAvatar } from "./GenericAvatar";

interface ProfileData {
  id: string;
  email: string;
  full_name: string | null;
  username: string | null;
  avatar_url: string | null;
  role: string;
}

interface ProfileMenuProps {
  transparent?: boolean;
  scheme?: "light" | "dark";
}

export function ProfileMenu({
  transparent,
  scheme = "dark",
}: ProfileMenuProps) {
  const { setTheme } = useTheme();
  const router = useRouter();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  // Stable client instance across renders
  const supabaseRef = useRef<ReturnType<typeof createSupabaseBrowserClient>>(
    createSupabaseBrowserClient()
  );
  const supabase = supabaseRef.current;

  const fetchProfile = useCallback(async () => {
    try {
      const res = await fetch("/api/auth/profile", { cache: "no-store" });
      if (res.ok) {
        const json = await res.json();
        setProfile(json.profile);
      } else {
        setProfile(null);
      }
    } catch {
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Auth state subscription for real-time UI updates
  useEffect(() => {
    const { data: subscription } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        setLoading(true);
        fetchProfile();
      } else if (event === "SIGNED_OUT") {
        setProfile(null);
      }
    });
    return () => subscription?.subscription?.unsubscribe();
  }, [supabase, fetchProfile]);

  const isAuthed = !!profile;
  const textColor = transparent
    ? scheme === "dark"
      ? "text-foreground"
      : "text-white"
    : "";
  const avatarBgClass = transparent
    ? scheme === "dark"
      ? "bg-background/40"
      : "bg-white"
    : "bg-background/40";
  const iconColorClass = transparent
    ? scheme === "dark"
      ? "text-foreground"
      : "text-black"
    : "";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={`flex items-center gap-2 p-1 h-10 rounded-full ${textColor}`}
          aria-label="Open profile menu"
        >
          <Avatar
            className={`h-8 w-8 border ${avatarBgClass} transition-colors`}
          >
            {loading ? (
              <AvatarFallback className="text-xs">
                <Loader className={`animate-spin ${iconColorClass}`} />
              </AvatarFallback>
            ) : isAuthed ? (
              <>
                <AvatarImage src={profile?.avatar_url || undefined} />
                <AvatarFallback className="text-xs">
                  <GenericAvatar
                    name={
                      profile?.full_name ||
                      profile?.username ||
                      profile?.email ||
                      "User"
                    }
                    email={profile?.email || "Anonymous"}
                    src={profile?.avatar_url || ""}
                    className="h-8 w-8"
                  />
                </AvatarFallback>
              </>
            ) : (
              <AvatarFallback className="text-xs">
                <Menu className={iconColorClass} />
              </AvatarFallback>
            )}
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="truncate">
          {isAuthed
            ? profile?.full_name || profile?.username || profile?.email
            : "You are browsing anonymously"}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {isAuthed ? (
          <>
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={() => router.push("/dashboard")}
            >
              <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
            </DropdownMenuItem>
            {profile?.role === "ADMIN" && (
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => router.push("/admin")}
              >
                <ShieldCheck className="mr-2 h-4 w-4" /> Admin
              </DropdownMenuItem>
            )}
          </>
        ) : (
          <>
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={() => router.push("/signin")}
            >
              Sign In
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={() => router.push("/signup")}
            >
              Create Account
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="cursor-pointer">
            <Sun className="rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0 mr-2 h-4 w-4" />
            <Moon className="absolute rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100 mr-2 h-4 w-4" />
            <span>Theme</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent className="min-w-32">
              <DropdownMenuItem
                onClick={() => setTheme("light")}
                className="cursor-pointer"
              >
                Light
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setTheme("dark")}
                className="cursor-pointer"
              >
                Dark
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setTheme("system")}
                className="cursor-pointer"
              >
                System
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
        {isAuthed && <DropdownMenuSeparator />}
        {isAuthed && (
          <DropdownMenuItem
            onClick={async () => {
              await supabase.auth.signOut();
              // Optimistically clear state; subscription will keep it cleared.
              setProfile(null);
              router.push("/signin");
            }}
            className="cursor-pointer"
          >
            <LogOut className="mr-2 h-4 w-4" /> Log out
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
